import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
} from '@mui/material';
import { Link } from 'react-router-dom';
import SchoolIcon from '@mui/icons-material/School';
import WomensIcon from '@mui/icons-material/Woman';
import PeopleIcon from '@mui/icons-material/People';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PersonIcon from '@mui/icons-material/Person';

const Activities = () => {
  return (
    <Box sx={{ minHeight: '80vh', py: 8, backgroundColor: '#f9fafb' }}>
      <Container maxWidth="lg">
        <Typography
          variant="h2"
          component="h1"
          sx={{
            textAlign: 'center',
            fontWeight: 700,
            mb: 2,
            color: '#f97316',
          }}
        >
          Our Activities
        </Typography>
        <Typography
          variant="h6"
          sx={{
            textAlign: 'center',
            color: 'text.secondary',
            mb: 2,
            maxWidth: '800px',
            mx: 'auto',
          }}
        >
          GBCN Foundation actively works across multiple domains to create positive social impact.
        </Typography>

        {/* Compact hero-style overview */}
        <Box
          sx={{
            mt: 3,
            mb: 6,
            p: 3,
            borderRadius: 3,
            background:
              'linear-gradient(135deg, rgba(249,115,22,0.06) 0%, rgba(37,99,235,0.04) 100%)',
            border: '1px solid #e5e7eb',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: 3,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Chip
              label="Programs at a glance"
              size="small"
              sx={{
                mb: 1.5,
                backgroundColor: '#ffedd5',
                color: '#9a3412',
                fontWeight: 600,
              }}
            />
            <Typography
              variant="h5"
              sx={{ fontWeight: 600, color: '#111827', mb: 1 }}
            >
              From classrooms to careers, culture to community.
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', maxWidth: 620 }}
            >
              Explore how our education, empowerment, mentorship, and culture programs work
              together to support learners, youth, and families across the network.
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1.5,
            }}
          >
            <Button
              component={Link}
              to="/events"
              variant="contained"
              sx={{
                backgroundColor: '#f97316',
                color: '#ffffff',
                textTransform: 'none',
                px: 3,
                '&:hover': { backgroundColor: '#ea580c' },
              }}
            >
              View related events
            </Button>
            <Button
              component={Link}
              to="/donate"
              variant="outlined"
              sx={{
                borderColor: '#111827',
                color: '#111827',
                textTransform: 'none',
                px: 3,
                '&:hover': {
                  borderColor: '#111827',
                  backgroundColor: 'rgba(15,23,42,0.04)',
                },
              }}
            >
              Support a program
            </Button>
          </Box>
        </Box>

        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: '100%',
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                transition: 'transform 0.3s, box-shadow 0.3s',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 8px 24px rgba(249, 115, 22, 0.15)',
                  borderColor: '#f97316',
                },
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <SchoolIcon
                  sx={{
                    fontSize: 56,
                    color: '#f97316',
                    mb: 2,
                  }}
                />
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#111827', mb: 2 }}>
                  Education Programs
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.8 }}>
                  Our education initiatives focus on providing quality learning opportunities and support to students 
                  from all backgrounds. We offer:
                </Typography>
                <Box component="ul" sx={{ pl: 2, color: 'text.secondary' }}>
                  <li>Scholarship programs for deserving students</li>
                  <li>Educational workshops and seminars</li>
                  <li>Study material and resource support</li>
                  <li>Career counseling and guidance</li>
                  <li>Skill development programs</li>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: '100%',
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                transition: 'transform 0.3s, box-shadow 0.3s',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 8px 24px rgba(249, 115, 22, 0.15)',
                  borderColor: '#f97316',
                },
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <WomensIcon
                  sx={{
                    fontSize: 56,
                    color: '#f97316',
                    mb: 2,
                  }}
                />
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#111827', mb: 2 }}>
                  Women Empowerment
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.8 }}>
                  Empowering women through various initiatives that promote independence, leadership, and growth:
                </Typography>
                <Box component="ul" sx={{ pl: 2, color: 'text.secondary' }}>
                  <li>Leadership development programs</li>
                  <li>Skill training and vocational courses</li>
                  <li>Entrepreneurship support and mentorship</li>
                  <li>Health and wellness workshops</li>
                  <li>Networking and community building</li>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: '100%',
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                transition: 'transform 0.3s, box-shadow 0.3s',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 8px 24px rgba(249, 115, 22, 0.15)',
                  borderColor: '#f97316',
                },
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <PeopleIcon
                  sx={{
                    fontSize: 56,
                    color: '#f97316',
                    mb: 2,
                  }}
                />
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#111827', mb: 2 }}>
                  Youth & Student Mentorship
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.8 }}>
                  Comprehensive mentorship programs designed to guide and support young individuals:
                </Typography>
                <Box component="ul" sx={{ pl: 2, color: 'text.secondary' }}>
                  <li>One-on-one mentorship programs</li>
                  <li>Career guidance and counseling</li>
                  <li>Academic support and tutoring</li>
                  <li>Personal development workshops</li>
                  <li>Internship and job placement assistance</li>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: '100%',
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                transition: 'transform 0.3s, box-shadow 0.3s',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 8px 24px rgba(249, 115, 22, 0.15)',
                  borderColor: '#f97316',
                },
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <MenuBookIcon
                  sx={{
                    fontSize: 56,
                    color: '#f97316',
                    mb: 2,
                  }}
                />
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#111827', mb: 2 }}>
                  Cultural & Sanskriti Activities
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.8 }}>
                  Preserving and promoting our rich cultural heritage through various activities:
                </Typography>
                <Box component="ul" sx={{ pl: 2, color: 'text.secondary' }}>
                  <li>Cultural festivals and celebrations</li>
                  <li>Sanskriti workshops and seminars</li>
                  <li>Traditional art and craft programs</li>
                  <li>Heritage documentation and preservation</li>
                  <li>Community cultural events</li>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Activities;

